import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AutoMatchResult,
  BankReconAdjustment,
  BankStatement,
  BankStatementLine,
  BankStatementListRow,
  BankStatementStatus,
  BankStatementWithChildren,
  BulkCreateBankStatementLines,
  CreateBankReconAdjustment,
  CreateBankStatement,
  ManualMatch,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Cache-key scopes keyed by companyId so multi-company switches give
// independent caches (same pattern as AR / AP).
const listKey = (companyId: string | undefined) => ['bank-statements', companyId] as const;
const oneKey = (companyId: string | undefined, id: string | undefined) =>
  ['bank-statements', companyId, id] as const;

export interface BankStatementFilters {
  bankAccountId?: string;
  status?: BankStatementStatus;
}

export function useBankStatements(
  companyId: string | undefined,
  filters: BankStatementFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.bankAccountId) qs.set('bankAccountId', filters.bankAccountId);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...listKey(companyId), filters],
    queryFn: () =>
      api<BankStatementListRow[]>(
        `/companies/${companyId}/bank-statements${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useBankStatement(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: oneKey(companyId, id),
    queryFn: () => api<BankStatementWithChildren>(`/companies/${companyId}/bank-statements/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateBankStatement(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBankStatement) =>
      apiJson<CreateBankStatement, BankStatement>(
        'POST',
        `/companies/${companyId}/bank-statements`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

export function useCreateBankStatementLines(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BulkCreateBankStatementLines }) =>
      apiJson<BulkCreateBankStatementLines, BankStatementWithChildren>(
        'POST',
        `/companies/${companyId}/bank-statements/${id}/lines`,
        data,
      ),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: oneKey(companyId, id) });
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

export function useAutoMatchStatement(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, AutoMatchResult>(
        'POST',
        `/companies/${companyId}/bank-statements/${id}/auto-match`,
      ),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: oneKey(companyId, id) });
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

export function useManualMatchStatement(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ManualMatch }) =>
      apiJson<ManualMatch, BankStatementWithChildren>(
        'POST',
        `/companies/${companyId}/bank-statements/${id}/match`,
        data,
      ),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: oneKey(companyId, id) });
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

export function useAddBankReconAdjustment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateBankReconAdjustment }) =>
      apiJson<CreateBankReconAdjustment, BankReconAdjustment>(
        'POST',
        `/companies/${companyId}/bank-statements/${id}/adjustments`,
        data,
      ),
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: oneKey(companyId, id) });
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useFinalizeBankStatement(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, BankStatementWithChildren>(
        'POST',
        `/companies/${companyId}/bank-statements/${id}/finalize`,
      ),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: oneKey(companyId, id) });
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

// Re-export types so consumers only need to import from this module.
export type {
  BankReconAdjustment,
  BankStatement,
  BankStatementLine,
  BankStatementListRow,
  BankStatementStatus,
  BankStatementWithChildren,
};
